import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { authService } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { setTokens, clearTokens } from "../../utils/tokenUtils";

const SamlCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [statusText, setStatusText] = useState("Completing sign-in...");

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const token = params.get("token");
  const refreshToken = params.get("refreshToken"); // Get refresh token from URL
  const success = params.get("success");

  useEffect(() => {
    const run = async () => {
      const isSuccess =
        success === null || success === "" || success === "true" || success === "1";

      if (!token || !isSuccess) {
        toast.error("Invalid sign-in callback. Please try again.");
        navigate("/login", { replace: true });
        return;
      }

      try {
        // Store tokens immediately so the axios interceptor attaches them
        setTokens(token, refreshToken);

        setStatusText("Fetching your profile...");
        const profileRes = await authService.profile();
        const data = profileRes?.data;

        const resolvedUser =
          data?.user ||
          data?.data?.user ||
          data?.profile ||
          data?.data ||
          data;

        if (!resolvedUser) {
          throw new Error("Profile response missing user");
        }

        // Pass both tokens to login context
        login({
          user: resolvedUser,
          token,
          refreshToken
        });

        const role = resolvedUser.role;
        const redirectPath =
          role === "admin"
            ? "/admin"
            : role === "teacher"
              ? "/teacher/dashboard"
              : "/student/dashboard";

        navigate(redirectPath, { replace: true });
      } catch (e) {
        console.error("SAML callback failed:", e);
        clearTokens();
        toast.error("Sign-in failed. Please try again.");
        navigate("/login", { replace: true });
      }
    };

    run();
  }, [token, refreshToken, success, navigate, login]);

  return (
    <div className="min-h-screen w-screen flex items-center justify-center bg-white dark:bg-gray-900 px-6">
      <div className="w-full max-w-md text-center">
        <div className="mx-auto mb-6 h-12 w-12 rounded-full border-4 border-blue-200 border-t-blue-600 animate-spin" />
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
          {statusText}
        </h1>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          Please wait while we log you in.
        </p>
      </div>
    </div>
  );
};

export default SamlCallback;
